// Redeems the one-time code at Spectrum's token endpoint.
//
// There is no broker in between and none is needed: PKCE is exactly what lets a
// client holding no secret do this from a browser. The verifier proves the
// caller is the frame that produced the challenge Zurich was given, which is the
// job a client secret would otherwise do.
//
// RFC 6749 field names on the wire — this is a standard OAuth exchange, not a
// private contract, so it stays in the standard's vocabulary.

const TIMEOUT_MS = 10000;

export type ScopedToken = {
  token: string;
  /** Seconds, when the server states it. Drives renewal — never assume an hour. */
  expiresIn?: number;
};

export class ExchangeError extends Error {}

// Deliberately hand-rolled rather than reaching for a schema library: up-spa has
// none, and the shape being checked is three fields.
const parseTokenResponse = (body: unknown): ScopedToken => {
  if (!body || typeof body !== "object") throw new ExchangeError("token response was not an object");
  const data = body as Record<string, unknown>;
  if (typeof data.access_token !== "string" || data.access_token.length === 0) {
    throw new ExchangeError("token response carried no access_token");
  }
  return {
    token: data.access_token,
    expiresIn: typeof data.expires_in === "number" ? data.expires_in : undefined,
  };
};

const postOnce = async (
  apiBaseUrl: string,
  clientId: string,
  code: string,
  codeVerifier: string
): Promise<ScopedToken> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${apiBaseUrl}/application/api/u/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        grant_type: "authorization_code",
        client_id: clientId,
        code,
        code_verifier: codeVerifier,
        // No client_secret: a public client has none. No redirect_uri: this flow
        // never redirects.
      }),
      signal: controller.signal,
      // No ambient credentials — the code and the verifier are the sole proof.
      credentials: "omit",
    });
    if (!response.ok) throw new ExchangeError(`exchange failed: ${response.status}`);
    return parseTokenResponse(await response.json());
  } finally {
    clearTimeout(timer);
  }
};

// One automatic retry, and only a transport failure can benefit from it: if the
// first request never reached Spectrum the code was not consumed. A code that
// did arrive is spent, so the retry fails and the caller falls through to its
// error path — which is correct. Replaying a consumed code must never succeed,
// and the server enforces that rather than trusting this comment.
export const exchangeCode = async (
  apiBaseUrl: string,
  clientId: string,
  authorizationCode: string,
  codeVerifier: string
): Promise<ScopedToken> => {
  try {
    return await postOnce(apiBaseUrl, clientId, authorizationCode, codeVerifier);
  } catch {
    return await postOnce(apiBaseUrl, clientId, authorizationCode, codeVerifier);
  }
};
